import { Ok, Result, Id } from "@/utils"
import { api_fetch, api_fetch_throws, HttpError, HttpResponse } from "./client"

export interface PendingTaskInfo {
  task_id: string
  assigned_to: string | null
  task_type: string
}

export interface ChecklistStepResponse {
  id: string
  checklist_id: string
  position: number
  title: string
  auto_complete_trigger: string | null
  is_completed: boolean
  completed_at: string | null
  completed_by: string | null
  completed_by_name: string | null
  auto_completed: boolean
  pending_task: PendingTaskInfo | null
}

export interface ChecklistInfo {
  id: string
  org_id: string | null
  deduction_id: string | null
  name: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface ChecklistResponse {
  checklist: ChecklistInfo
  steps: ChecklistStepResponse[]
}

export interface ChecklistStepData {
  id?: string
  title: string
  auto_complete_trigger: string | null
  position: number
}

export interface ChecklistTemplateResponse {
  checklist: ChecklistInfo
  steps: {
    id: string
    checklist_id: string
    position: number
    title: string
    auto_complete_trigger: string | null
    created_at: string
  }[]
}

export type ChecklistScope = "deduction" | "category" | "system_category" | "template"

export interface CreateChecklistRequest {
  name: string
  steps: { title: string; auto_complete_trigger: string | null }[]
  scope: ChecklistScope
  deduction_id?: string
  category_ids?: string[]
}

export interface UpdateChecklistRequest {
  name?: string
  steps?: ChecklistStepData[]
}

export const AUTO_COMPLETE_TRIGGERS = [
  { value: "triage_completed", label: "AI Wizard completed" },
  { value: "backup_requested", label: "Backup requested" },
  { value: "backup_received", label: "Backup received" },
  { value: "dispute_filed", label: "Dispute filed" },
  { value: "validated", label: "Deduction validated" },
] as const

export async function get_deduction_checklist(deduction_id: Id): Promise<ChecklistResponse | null> {
  return api_fetch_throws<ChecklistResponse | null>(`/deduction/${deduction_id}/checklist`)
}

export async function create_checklist(
  data: CreateChecklistRequest
): Promise<Result<HttpResponse<ChecklistResponse>, HttpError>> {
  return api_fetch<ChecklistResponse>("/checklist", {
    body: data,
  })
}

export async function update_checklist(
  checklist_id: Id,
  data: UpdateChecklistRequest
): Promise<Result<HttpResponse<ChecklistResponse>, HttpError>> {
  return api_fetch<ChecklistResponse>(`/checklist/${checklist_id}`, {
    method: "PUT",
    body: data,
  })
}

export async function delete_checklist(
  checklist_id: Id
): Promise<Result<HttpResponse<{ deleted: boolean }>, HttpError>> {
  return api_fetch<{ deleted: boolean }>(`/checklist/${checklist_id}`, {
    method: "DELETE",
  })
}

export async function list_checklist_templates(): Promise<ChecklistTemplateResponse[]> {
  return api_fetch_throws<ChecklistTemplateResponse[]>("/checklist/templates")
}

export async function complete_checklist_step(
  step_id: Id,
  deduction_id: Id
): Promise<Result<HttpResponse<unknown>, HttpError>> {
  return api_fetch(`/checklist/step/${step_id}/complete`, {
    body: { deduction_id },
  })
}

export async function uncomplete_checklist_step(
  step_id: Id,
  deduction_id: Id
): Promise<Result<HttpResponse<unknown>, HttpError>> {
  return api_fetch(`/checklist/step/${step_id}/uncomplete`, {
    body: { deduction_id },
  })
}
